import type { GameStats } from "../../engine/gameStats";
import { Strings } from "../../engine/strings";

export interface StatsRow {
	/** already localized */
	label: string;
	/** already formatted (e.g., "1,234", "01:23") */
	value: string;
	section?: string;
	/** Dimmed top-N breakdown beneath a total. */
	isSubEntry?: boolean;
}

export interface StatsPanelData {
	rows: StatsRow[];
	/** Null when stats has no startedAt. */
	headerLine: string | null;
}

export interface StatsViewModelOptions {
	showCombat?: boolean;
	showExploration?: boolean;
	showItems?: boolean;
	showMisc?: boolean;
	showDerived?: boolean;
	/** Tests pass a fixed Date for determinism. */
	now?: Date;
}

const SECTION_KEYS = {
	combat: "stats_sectionCombat",
	exploration: "stats_sectionExploration",
	items: "stats_sectionItems",
	survival: "stats_sectionSurvival",
	highlights: "stats_sectionHighlights",
} as const;

/** Pure — GameStats in, plain rows out. */
export class StatsViewModel {
	computePanelData(stats: GameStats, options?: StatsViewModelOptions): StatsPanelData {
		const showCombat = options?.showCombat !== false;
		const showExploration = options?.showExploration !== false;
		const showItems = options?.showItems !== false;
		const showMisc = options?.showMisc !== false;
		const showDerived = options?.showDerived !== false;
		const now = options?.now ?? new Date();

		const rows: StatsRow[] = [];
		const tracker = stats.tracker;

		if (showCombat) {
			this.appendCombatRows(rows, stats, tracker);
		}
		if (showExploration) {
			this.appendExplorationRows(rows, stats, tracker);
		}
		if (showItems) {
			this.appendItemsRows(rows, tracker);
		}
		if (showMisc) {
			this.appendSurvivalRows(rows, tracker);
		}
		if (showDerived) {
			this.appendHighlightsRows(rows, stats);
		}

		return {
			rows,
			headerLine: this.computeHeaderLine(stats, now),
		};
	}

	private appendCombatRows(rows: StatsRow[], stats: GameStats, tracker: GameStats["tracker"]): void {
		const totalKills = sumMap(tracker.mobsKilledByType);
		const totalDamage = sumMap(tracker.damageReceivedBySource);
		const totalShots = sumMap(tracker.shotsFiredByWeapon);
		if (totalKills === 0 && totalDamage === 0 && totalShots === 0 && !stats.causeOfDeath) {
			return;
		}
		const section = label(SECTION_KEYS.combat);

		if (totalKills > 0) {
			rows.push({ label: label("stats_labelMobsKilled"), value: String(totalKills), section });
			this.pushSubEntries(rows, section, getTopEntries(tracker.mobsKilledByType, 3), thingName);
		}
		if (totalDamage > 0) {
			rows.push({ label: label("stats_labelDamageReceived"), value: String(totalDamage), section });
			this.pushSubEntries(rows, section, getTopEntries(tracker.damageReceivedBySource, 3), thingName);
		}
		if (totalShots > 0) {
			rows.push({ label: label("stats_labelShotsFired"), value: String(totalShots), section });
			this.pushSubEntries(rows, section, getTopEntries(tracker.shotsFiredByWeapon, 3), weaponName);
		}
		if (stats.causeOfDeath) {
			rows.push({
				label: label("stats_labelCauseOfDeath"),
				value: thingName(stats.causeOfDeath.sourceKey),
				section,
			});
		}
	}

	private appendExplorationRows(rows: StatsRow[], stats: GameStats, tracker: GameStats["tracker"]): void {
		const turns = stats.getTotalTurnsElapsed();
		const tiles = tracker.tilesWalked;
		const levels = stats.levelsVisited;
		if (turns === 0 && tiles === 0 && levels === 0) {
			return;
		}
		const section = label(SECTION_KEYS.exploration);

		if (turns > 0) rows.push({ label: label("stats_labelTurnsElapsed"), value: String(turns), section });
		if (tiles > 0) rows.push({ label: label("stats_labelTilesWalked"), value: String(tiles), section });
		if (levels > 0) rows.push({ label: label("stats_labelLevelsVisited"), value: String(levels), section });

		const favLevel = stats.getFavoriteLevelNumber();
		if (favLevel !== null) {
			rows.push({ label: label("stats_labelFavoriteLevel"), value: String(favLevel), section });
		}
	}

	private appendItemsRows(rows: StatsRow[], tracker: GameStats["tracker"]): void {
		const totalAmmo = sumMap(tracker.ammoCollectedByWeapon);
		const containers = tracker.containersOpened;
		const obstacles = tracker.obstaclesDestroyed;
		if (totalAmmo === 0 && containers === 0 && obstacles === 0) {
			return;
		}
		const section = label(SECTION_KEYS.items);

		if (totalAmmo > 0) {
			rows.push({ label: label("stats_labelAmmoCollected"), value: String(totalAmmo), section });
			this.pushSubEntries(rows, section, getTopEntries(tracker.ammoCollectedByWeapon, 3), weaponName);
		}
		if (containers > 0) rows.push({ label: label("stats_labelContainersOpened"), value: String(containers), section });
		if (obstacles > 0) rows.push({ label: label("stats_labelObstaclesDestroyed"), value: String(obstacles), section });
	}

	private appendSurvivalRows(rows: StatsRow[], tracker: GameStats["tracker"]): void {
		const lowestHp = tracker.lowestHpReached;
		const healing = tracker.healingReceived;
		if (lowestHp < 0 && healing === 0) {
			return;
		}
		const section = label(SECTION_KEYS.survival);

		if (lowestHp >= 0) rows.push({ label: label("stats_labelLowestHp"), value: String(lowestHp), section });
		if (healing > 0) rows.push({ label: label("stats_labelHealingReceived"), value: String(healing), section });
	}

	private appendHighlightsRows(rows: StatsRow[], stats: GameStats): void {
		const mostUsed = stats.getMostUsedWeapon();
		const mostLethal = stats.getMostLethalWeapon();
		if (!mostUsed && !mostLethal && !stats.startedAt) {
			return;
		}
		const section = label(SECTION_KEYS.highlights);

		if (mostUsed) rows.push({ label: label("stats_labelMostUsedWeapon"), value: weaponName(mostUsed), section });
		if (mostLethal) rows.push({ label: label("stats_labelMostLethalWeapon"), value: weaponName(mostLethal), section });

		if (stats.startedAt) {
			rows.push({
				label: label("stats_labelGameStarted"),
				value: new Date(stats.startedAt).toLocaleString(),
				section,
			});
		}
	}

	private pushSubEntries(
		rows: StatsRow[],
		section: string,
		entries: [string, number][],
		nameResolver: (key: string) => string,
	): void {
		for (const [key, count] of entries) {
			rows.push({
				label: nameResolver(key),
				value: String(count),
				section,
				isSubEntry: true,
			});
		}
	}

	private computeHeaderLine(stats: GameStats, now: Date): string | null {
		if (!stats.startedAt) return null;
		const startMs = Date.parse(stats.startedAt);
		if (Number.isNaN(startMs)) return null;
		const durationMs = now.getTime() - startMs;
		if (durationMs < 0) return null;
		const durationLabel = formatDurationMmSs(durationMs);
		const prefix = Strings.get("stats_labelRunDuration") ?? "Run duration";
		return `${prefix}: ${durationLabel}`;
	}
}

function getTopEntries(map: Map<string, number>, n: number): [string, number][] {
	return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, n);
}

function sumMap(map: Map<string, number>): number {
	let total = 0;
	for (const v of map.values()) total += v;
	return total;
}

function label(key: string): string {
	return Strings.get(key) ?? key;
}

function thingName(key: string): string {
	return Strings.get("tname_" + key) || key;
}

function weaponName(key: string): string {
	return Strings.get("wname_" + key) || key;
}

/** Durations >= 1h spill into minutes (e.g., 1h30m becomes "90:00"). */
export function formatDurationMmSs(ms: number): string {
	if (!Number.isFinite(ms) || ms <= 0) return "00:00";
	const totalSeconds = Math.floor(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
